import { useState, useRef } from 'react';
import { Tooltip } from '@mui/material';
import moment from 'moment';
import { REMINDER_PRESETS } from '../Types/ReminderOffset';
import { useUserSetting } from '../../contexts/UserSettingsContext';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import CalEventRow from './CalEventRow';
import CalEventDatePicker from './CalEventDatePicker';
import CalEventTimePicker from './CalEventTimePicker';

/**
 * CalEventFields — all editable rows for CalEventDialog.
 * @param {object} props
 * @param {CalEventEditState} props.current - current field values
 * @param {function} props.onChange - fn(field, value) called on any field change
 * @param {object} props.reminderEditor - { selected, custom, setPreset, setCustom } from useReminderEditor
 */
export default function CalEventFields({ current, onChange, reminderEditor }) {
    const { selected, custom, setPreset, setCustom } = reminderEditor;

    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [timePickerOpen, setTimePickerOpen] = useState(false);
    const dateRowRef = useRef(null);
    const timeRowRef = useRef(null);
    const dateFormat = useUserSetting('dateFormat');
    const formattedDate = current.date ? moment(current.date, 'YYYY-MM-DD').format(dateFormat) : '';

    /**
     * Opens the date picker, anchored below the date row.
     */
    const handleOpenDatePicker = () => {
        const rect = dateRowRef.current?.getBoundingClientRect();
        setDatePickerOpen(rect ? { top: rect.bottom + 4, left: rect.left } : false);
    };

    /**
     * Opens the time picker, anchored below the time row.
     */
    const handleOpenTimePicker = () => {
        const rect = timeRowRef.current?.getBoundingClientRect();
        setTimePickerOpen(rect ? { top: rect.bottom + 4, left: rect.left } : false);
    };

    /**
     * Time picker saves both date and time at once.
     * @param {string} iso - YYYY-MM-DD
     * @param {string} hhmm - HH:MM
     */
    const handleTimeSave = (iso, hhmm) => {
        onChange('date', iso);
        onChange('time', hhmm);
    };

    return (
        <>
            <CalEventRow label="Label" value={current.label} onChange={(v) => onChange('label', v)} />

            {/* Date — clickable, opens CalEventDatePicker */}
            <div className="ce-row" ref={dateRowRef}>
                <span className="ce-label">Event Date</span>
                <Tooltip
                    //
                    title={`The target date is ${formattedDate} — make sure you're ready!`}
                    componentsProps={tooltipSxElevated}
                    placement="top"
                >
                    <input
                        className="ce-input"
                        type="text"
                        value={current.date}
                        onClick={handleOpenDatePicker}
                        onFocus={handleOpenDatePicker}
                        readOnly
                        placeholder="YYYY-MM-DD"
                        style={{ cursor: 'pointer' }}
                    />
                </Tooltip>
            </div>
            {datePickerOpen && (
                <CalEventDatePicker
                    anchorPos={datePickerOpen}
                    value={current.date}
                    onSelect={(iso) => onChange('date', iso)}
                    onClose={() => setDatePickerOpen(false)}
                />
            )}

            {/* Time — clickable, opens CalEventTimePicker */}
            <div className="ce-row" ref={timeRowRef}>
                <span className="ce-label">Reminder Time</span>
                <input
                    className="ce-input"
                    type="text"
                    value={current.time}
                    onClick={handleOpenTimePicker}
                    onFocus={handleOpenTimePicker}
                    readOnly
                    placeholder="HH:MM"
                    style={{ cursor: 'pointer' }}
                />
            </div>
            {timePickerOpen && (
                <CalEventTimePicker
                    anchorPos={timePickerOpen}
                    dateValue={current.date}
                    timeValue={current.time}
                    onSave={handleTimeSave}
                    onClose={() => setTimePickerOpen(false)}
                />
            )}

            <CalEventRow label="Cat" value={current.cat} onChange={(v) => onChange('cat', v)} />
            <CalEventRow label="Description" value={current.desc} onChange={(v) => onChange('desc', v)} multiline />

            {/* Remind me — preset dropdown + custom offset */}
            <div className="ce-row">
                <span className="ce-label">Remind me</span>
                <select className="ce-select" value={selected} onChange={(e) => setPreset(e.target.value)}>
                    {REMINDER_PRESETS.map((p) => (
                        <option key={p.value} value={p.value}>
                            {p.label}
                        </option>
                    ))}
                </select>
            </div>

            {selected === '__custom__' && (
                <div className="ce-row">
                    <span className="ce-label">Offset (e.g. 45m, 3h, 2d)</span>
                    <input className="ce-input" type="text" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="45m" />
                </div>
            )}
        </>
    );
}
